/**
 * cover-rework (老闆 2026-06「建築很醜、沒效果、超級爛 → 重做成有意義的」):
 *
 * 戰術掩體 (Cover) — 場景上有 HP 的「擋彈牆」。魔王彈/敵彈打中掩體會被擋下
 * (不會穿過去傷玩家),掩體本身扣血;被彈幕打多了會破 (HP 歸零) → 該安全區
 * 消失,逼玩家換位。玩家躲在掩體後 = 真正的戰術價值,而非靜止裝飾。
 *
 * 為什麼重做 (根因):
 *   - 舊版掩體是「靜止裝飾」放牆角 (x45-80%),不擋彈不擋敵、對戰鬥零影響,
 *     玩家忙著閃彈幕打魔王根本不會理它 → 老闆「沒效果」。
 *   - 舊版用 Graphics 畫陽春幾何 (咖啡方塊/灰圓/青菱形),跟精緻卡通坦克 +
 *     卡通地圖放一起很突兀 → 老闆「很醜」。
 *
 * 新版設計原則 (對齊 boss memory):
 *   - 純遊戲機制:不彈題目、不寫回修煉值 (擊毀只接戰鬥內 run-xp,加分非主誘因)。
 *   - 視覺卡通:用 Pollinations 生的 3 張卡通掩體 sprite (沙包/能量盾/石牆),
 *     畫風跟坦克 + 地圖統一;被打血量低時變暗 + 命中閃白 (破損視覺)。
 *   - 平衡 (K-3):HP 適中 (被彈幕打幾秒會破)、數量 2-3 個、放在「玩家活動區與
 *     魔王彈來向之間」(玩家右前方) 才擋得到飛來的彈;會破就不會無腦躲一個點過關。
 *
 * 這個檔只放「純資料 + 邏輯」(HP/扣血/存活/AABB 擋彈判定),不碰 Phaser,讓
 * unit test 可以 headless 直接驗證。視覺與場景 wiring 在 horizontal-battle-scene.ts。
 */

/** 掩體外觀種類 (對應 public/assets/covers/cover-*.png 三張卡通 sprite)。 */
export type DestructibleKind = 'sandbag' | 'shield' | 'stone';

/**
 * 各種類預設 HP。掩體是「會被彈幕打爆的擋彈牆」,HP 要讓玩家躲幾秒就得換位
 * (魔王彈 damage ~15;~3-5 發破一面 sandbag,stone 最耐、shield 最脆)。
 * K-3:不會永久安全 (逼走位),也不會一發就破 (還是有掩護價值)。
 */
// cover-balance (老闆「障礙物太強,魔王攻擊都被擋,我可以一直打魔王」):
// 大幅下調 HP — 掩體是「臨時喘息」不是「無敵牆」,被彈幕打幾發就破,逼玩家
// 持續走位、不能躲一點無腦輸出。魔王彈 dmg~15 → 1-3 發就破。
export const DESTRUCTIBLE_HP: Record<DestructibleKind, number> = {
  sandbag: 24, // 中等 (~2 發)
  shield: 15, // 最脆 (能量盾,1 發就閃爆)
  stone: 36, // 最耐 (石牆,~3 發)
};

/** 擊毀掩體給的 run-xp 倍率 (相對一般小兵;少量,加分非主誘因不喧賓奪主)。 */
export const DESTRUCTIBLE_XP_MULT = 0.5;

/** 對應的卡通 sprite 貼圖 key (場景 preload 用同名載入)。 */
export const COVER_TEXTURE_KEYS: Record<DestructibleKind, string> = {
  sandbag: 'cover-sandbag',
  shield: 'cover-shield',
  stone: 'cover-stone',
};

export interface DestructibleInit {
  x: number;
  y: number;
  kind: DestructibleKind;
  /** 視覺半徑 / 圓形命中容差用 (px)。 */
  radius: number;
  /**
   * 擋彈用 AABB 半寬 (px)。掩體是「牆」→ 通常比半高窄、比 radius 略大。
   * 省略 = 用 radius (向後相容圓形時期的呼叫端)。
   */
  halfW?: number;
  /** 擋彈用 AABB 半高 (px)。省略 = 用 radius。 */
  halfH?: number;
  /** 覆寫預設 HP (省略 = 用 DESTRUCTIBLE_HP[kind])。 */
  hp?: number;
}

/**
 * 戰術掩體 entity。HP>0 = alive;takeDamage 扣血,歸零 → alive=false。
 * 擋彈判定用 AABB (blocks);圓形 hitTest 保留向後相容 (玩家彈命中掩體沿用)。
 */
export class Destructible {
  x: number;
  y: number;
  readonly kind: DestructibleKind;
  readonly radius: number;
  /** 擋彈 AABB 半寬 (px)。 */
  readonly halfW: number;
  /** 擋彈 AABB 半高 (px)。 */
  readonly halfH: number;
  readonly hpMax: number;
  hp: number;
  alive = true;
  /** 最近一次被命中的時間戳 (ms);場景用來畫命中閃白。-Infinity = 從未被打。 */
  lastHitMs = -Infinity;

  constructor(init: DestructibleInit) {
    this.x = init.x;
    this.y = init.y;
    this.kind = init.kind;
    this.radius = init.radius;
    this.halfW = init.halfW ?? init.radius;
    this.halfH = init.halfH ?? init.radius;
    const base = init.hp ?? DESTRUCTIBLE_HP[init.kind];
    this.hpMax = base;
    this.hp = base;
  }

  /** 0..1 剩餘血量比例 (場景畫破損程度用)。 */
  get hpFrac(): number {
    return this.hpMax > 0 ? Math.max(0, this.hp) / this.hpMax : 0;
  }

  /**
   * 子彈點 (px) 是否命中此掩體 (圓形碰撞，含子彈半徑容差)。
   * 保留給「玩家彈命中掩體」的舊路徑 (向後相容)。
   * @param bulletRadius 子彈碰撞半徑容差 (預設 4)
   */
  hitTest(px: number, py: number, bulletRadius = 4): boolean {
    if (!this.alive) return false;
    const dx = px - this.x;
    const dy = py - this.y;
    const rr = this.radius + bulletRadius;
    return dx * dx + dy * dy <= rr * rr;
  }

  /**
   * cover-rework 核心:子彈點 (px) 是否被此掩體「擋下」(AABB 矩形碰撞)。
   * 牆是矩形,用 AABB 比圓形更貼合視覺、擋彈更可預期 (K-3「躲在牆後就擋得到」)。
   * @param bulletRadius 子彈碰撞半徑容差 (預設 4)
   */
  blocks(px: number, py: number, bulletRadius = 4): boolean {
    if (!this.alive) return false;
    return (
      Math.abs(px - this.x) <= this.halfW + bulletRadius &&
      Math.abs(py - this.y) <= this.halfH + bulletRadius
    );
  }

  /**
   * 扣血。回傳「這次是否把它打爆」(true = 剛好歸零本次)。
   * 歸零後 alive=false (場景據此移除 + 播爆炸 + 給 XP)。
   */
  takeDamage(amount: number, nowMs = 0): boolean {
    if (!this.alive) return false;
    this.lastHitMs = nowMs;
    this.hp -= Math.max(0, amount);
    if (this.hp <= 0) {
      this.hp = 0;
      this.alive = false;
      return true;
    }
    return false;
  }
}

/**
 * cover-rework: 產生「戰術掩體」佈局 (純資料,可測)。
 *
 * 關鍵 — 位置要「擋得到彈」:玩家在左 (x 60~480 活動),魔王在右側往左發彈。
 * 掩體必須放在「玩家活動區與魔王彈來向之間」= 玩家右前方,垂直散開,玩家移到
 * 某面牆後就能用它擋下飛來的彈幕。放太右 (牆角) 玩家躲不到 = 舊版的錯;放太左
 * 又擋死玩家自己的射線。取 x 中段 (45%~55% 寬),做成「直立牆」(窄而高) → 既擋
 * 飛來的橫向彈、又不會像一道橫牆把玩家射線整排擋死。
 *
 * 數量預設 3,垂直均分 (上/中/下) 讓玩家上下走位都至少有一面可躲;牆之間留縫
 * 讓玩家子彈和走位能穿過,不會被一道牆封死。
 *
 * @param count   掩體數 (預設 3;2-3 個剛好,別洗版)
 * @param fieldW  場景寬
 * @param fieldH  場景高
 * @param rand    亂數源 (測試可注入)
 */
export function makeDestructibleLayout(
  count = 2,
  fieldW: number,
  fieldH: number,
  rand: () => number = Math.random,
): DestructibleInit[] {
  const kinds: DestructibleKind[] = ['sandbag', 'stone', 'shield'];
  const out: DestructibleInit[] = [];
  const n = Math.max(0, Math.floor(count));
  if (n === 0) return out;

  // x:放在玩家右前方 (45%~55% 寬) — 玩家活動上限 X_MAX≈60% 寬,掩體在它左前方
  // 一點,玩家退到牆後就能擋下右方飛來的彈;又不會太右讓玩家躲不到。
  const xMin = fieldW * 0.45;
  const xMax = fieldW * 0.55;
  // y:垂直均分成 n 段,每面牆落在各段中央 (上/中/下),避免擠在一起。
  const yTop = fieldH * 0.2;
  const yBot = fieldH * 0.8;
  const span = yBot - yTop;

  // 牆形:窄而矮 (cover-balance:老闆「太強擋住全部魔王彈」)。半寬小 (~20) 不擋死
  // 射線,半高下調 46→30,讓掩體只擋「一小塊」而非整條彈幕來向,垂直段之間留大縫
  // → 玩家要主動退到牆後一小區才擋得到,不能整排無敵躺打。
  const HALF_W = 20;
  const HALF_H = 30;

  for (let i = 0; i < n; i++) {
    const kind = kinds[i % kinds.length];
    // 每面牆在自己 y 段內小幅隨機,x 在窄帶內小幅隨機,避免每場一模一樣。
    const segCenter = n === 1 ? (yTop + yBot) / 2 : yTop + (span * (i + 0.5)) / n;
    const jitterY = (rand() - 0.5) * (span / n) * 0.3;
    out.push({
      x: xMin + rand() * (xMax - xMin),
      y: segCenter + jitterY,
      kind,
      radius: Math.max(HALF_W, HALF_H), // 視覺/圓判定容差用最大邊
      halfW: HALF_W,
      halfH: HALF_H,
    });
  }
  return out;
}
